import { useToast, UseDisclosureProps, UseDisclosureReturn } from '@chakra-ui/react';
import { Modal, ModalContent } from '../ui/modal';
import { ModalNames, useModalState } from '../../misc/wrappers/modal-provider';
import { SubmitHandler, useForm } from 'react-hook-form';
import { subscribeSchema } from 'shared/validation/schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@apollo/client';
import { useAuth } from 'shared/misc/hooks/useAuth';
import { gql } from 'shared/__generated__';
import { TWLink } from 'web2/app/components/link';
import { Button } from 'web2/app/components/button';
import { useRouter } from '@tanstack/react-router';
import PerfInput from '../ui/perf-input';
import { useFacebookPixel } from 'shared/misc/providers/FacebookPixelProvider';

type Inputs = {
  email: string;
};

export default function SimpleSubscribeModal({
  redirectTo,
  modalState,
}: {
  redirectTo?: string | null;
  modalState?: UseDisclosureReturn;
}) {
  const { isOpen, onOpen, onClose } = modalState || useModalState(ModalNames.SIMPLE_SUBSCRIBE);

  const { signIn, ...auth } = useAuth();
  const toast = useToast();
  const { trackFacebookEvent } = useFacebookPixel();

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<Inputs>({
    resolver: yupResolver(subscribeSchema),
  });

  const [mutation, mutationInfo] = useMutation(SIGN_UP_EMAIL_ONLY);

  const finishSignUpModalState = useModalState(ModalNames.FINISH_SIGN_UP);

  const router = useRouter();

  console.log({ redirectTo });

  const onSubmit: SubmitHandler<Inputs> = async ({ email }: Inputs, event) => {
    event?.preventDefault();
    await mutation({
      variables: {
        input: { email },
      },
      onCompleted: async (data) => {
        const token = data?.signUpEmailOnly;
        if (token) await signIn(token);
        trackFacebookEvent('CompleteRegistration', {
          status: true,
        });
        toast({
          title: 'Subscribed',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
        onClose();
        console.log({ redirectTo });

        if (redirectTo) {
          router.navigate({ to: redirectTo });
        } else {
          finishSignUpModalState.onOpen();
        }
      },
      onError: (e) => {
        if (e.graphQLErrors[0]?.extensions?.surfaceToUser) {
          const { field, redirectToEmailSignIn } = e.graphQLErrors[0].extensions;

          if (redirectToEmailSignIn) {
            toast({
              title: 'You already have an account, check your email for a code.',
              status: 'success',
              duration: 3000,
              isClosable: true,
              position: 'top',
            });

            router.navigate({
              to: `/sign-in?context=code-sign-in&email=${encodeURIComponent(email)}`,
            });
          }

          if (field === 'email') {
            // console.log('error with email', e.message);
            return;
          }
        }
      },
    });
  };

  return (
    <Modal open={isOpen} onOpenChange={(o) => (o ? onOpen() : onClose())}>
      <ModalContent className="md:max-w-[700px] overflow-y-scroll">
        <div className="overflow-y-scroll pb-6 pt-6">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col base:p-1.5 base:gap-4.5 md:gap-6"
          >
            <div className="typography-heading-lg text-brand-title-background base:text-[36px] md:text-[36px] pb-[12px]">
              Join the 130,000+ enhancing their taste with Perfectly Imperfect.
            </div>
            <div className="self-center w-full">
              <div className="flex items-center w-full">
                <PerfInput
                  autofocus
                  placeholder="ENTER YOUR EMAIL"
                  className="flex-1"
                  {...register('email')}
                />
                <div className="bg-transparent border-l-0 rounded-none border-brand-main p-1">
                  <Button
                    type="submit"
                    className="mb-1.5 text-[15px]"
                    variant="primary"
                    disabled={mutationInfo.loading}
                  >
                    {mutationInfo.loading ? 'Loading...' : 'Sign Up'}
                  </Button>
                </div>
              </div>
              {errors.email && (
                <div className="text-red-500 font-normal">{errors.email?.message}</div>
              )}
              <button
                className="typography-action-sm opacity-40 pb-[16px] md:pb-0 hover:opacity-100 cursor-pointer leading-[150%]"
                onClick={onClose}
              >
                Skip for now
              </button>
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="typography-heading-sm leading-[150%]">A Cult-favorite Newsletter</div>
              <div className="typography-body-sm leading-[150%] text-brand-title-background">
                Featuring guests like Olivia Rodrigo, Francis Ford Coppola, Charli XCX, Ayo Edebiri,
                Michael Imperioli, Emma Chamberlain, Clairo, Ice Spice, Molly Ringwald, Weyes Blood,
                Jeremy O. Harris, Conner O'Malley, Rayne Fisher Quann, Julian Casablancas, Alexa
                Chung, Mac Demarco, Moses Sumney, Omar Apollo, Suki Waterhouse, Narwuar, The Dare,
                and hundreds more in{' '}
                <TWLink
                  to="/read"
                  className="typography-body-sm leading-[150%] text-brand-title-background"
                  variant="stylizedNoDefaultBorder"
                >
                  the Perfectly Imperfect Archive.
                </TWLink>
              </div>
              <div className="typography-body-sm leading-[150%] text-brand-title-background mb-[12px]">
                The history of PI is well-documented by The New York Times, NYMag, Vanity Fair,
                Interview Mag, Flaunt, Office, The Verge, and NPR.
              </div>
              <div className="typography-heading-sm leading-[150%]">
                A one-of-a-kind social network for taste
              </div>
              <div className="typography-body-sm leading-[150%] text-brand-title-background">
                It's like if you took all the best parts of MySpace, Letterboxd, Reddit, Twitter,
                Tumblr, and Pinterest, and combined them into the perfect site.
              </div>
              <div className="typography-body-sm leading-[150%] text-brand-title-background mb-[12px]">
                Share recommendations, discover new things, theme your profile, follow your friends,
                bookmark content for later, and much more.
              </div>
              <a
                href="https://apps.apple.com/us/app/pi-fyi-by-perfectly-imperfect/id6474037926"
                className="mb-[12px]"
                target="_blank"
              >
                <img
                  src="https://files.pi.fyi/App_Store_Badge.png"
                  alt="star-blue"
                  width={500}
                  height={160}
                  style={{
                    maxWidth: '150px',
                  }}
                />
              </a>
              <div className="typography-heading-sm leading-[150%]">Escape the algorithm.</div>
              <div className="typography-body-sm leading-[150%] text-brand-title-background mb-[12px]">
                Embrace serendipity and connect with real people.
              </div>
            </div>
          </form>
          <div className="flex justify-between items-center w-full mt-[24px]">
            <div className="typography-body-sm text-brand-lightgrey w-fit">
              <TWLink
                to="/sign-in"
                variant="stylizedNoDefaultBorder"
                className="typography-heading-sm"
              >
                Sign in
              </TWLink>
            </div>
            <div className="typography-body-sm text-brand-lightgrey w-fit">
              <TWLink
                to="/what-is-this"
                variant="stylizedNoDefaultBorder"
                className="typography-heading-sm"
              >
                Learn more
              </TWLink>
            </div>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
}

const SIGN_UP_EMAIL_ONLY = gql(/* GraphQL */ `
  mutation signUpEmailOnlyModal($input: SignUpEmailOnlyInput!) {
    signUpEmailOnly(input: $input)
  }
`);
